import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, MessageCircleHeart, BellRing, CalendarHeart, Moon, HandHeart, Gauge } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { logError } from "@/lib/log";
import { NudgeDays } from "@/components/NudgeDays";

export const AlertToggleCard = ({ field, icon: I, title, desc, label, testid, children }) => {
  const [on, setOn] = useState(null);

  useEffect(() => {
    api.get("/recap/preview").then((r) => setOn(r.data[field] !== false)).catch((e) => logError("Settings load failed:", e));
  }, [field]);

  const toggle = async (v) => {
    setOn(v);
    try {
      await api.put("/recap/settings", { [field]: v });
      toast.success(`${label} ${v ? "on" : "off"}`);
    } catch (e) {
      setOn(!v);
      toast.error(errMsg(e));
    }
  };

  if (on === null) return null;
  return (
    <div className="mm-card p-5" data-testid={`${testid}-card`}>
      <div className="flex justify-between items-center gap-3">
        <div>
          <div className="font-semibold flex items-center gap-2"><I size={18} /> {title}</div>
          <div className="text-sm text-mm-muted">{desc}</div>
        </div>
        <Switch checked={on} onCheckedChange={toggle} data-testid={`${testid}-toggle`} />
      </div>
      {children}
    </div>
  );
};

export const LoginAlertsCard = () => (
  <AlertToggleCard field="login_alerts_enabled" icon={ShieldCheck} title="New-device login alerts" label="New-device login alerts" testid="login-alerts"
    desc="Get an email whenever your account is signed in from a browser or device we haven't seen before." />
);

export const ReplyAlertsCard = () => (
  <AlertToggleCard field="reply_alerts_enabled" icon={MessageCircleHeart} title="Supporter reply alerts" label="Supporter reply alerts" testid="reply-alerts"
    desc="Get an email whenever a supporter writes back in one of your conversations." />
);

export const NotePushCard = () => (
  <AlertToggleCard field="note_push_enabled" icon={BellRing} title="Supporter note notifications" label="Supporter note notifications" testid="note-push"
    desc="A phone notification when a supporter sends a note or replies. The message stays off your lock screen. Uses the devices you set up in Reminders." />
);

export const WeekdayNudgeCard = () => (
  <AlertToggleCard field="weekday_nudge_enabled" icon={CalendarHeart} title="Toughest-day morning nudge" label="Toughest-day nudge" testid="weekday-nudge"
    desc="At 8am on the weekday that tends to feel heaviest for you, a gentle phone notification with the coping skill that helps you most. Automatic mode needs 3+ check-ins on that day.">
    <NudgeDays />
  </AlertToggleCard>
);

export const EveningPlanCard = () => (
  <AlertToggleCard field="streak_reminder_enabled" icon={Moon} title="Evening plan reminder" label="Evening plan reminder" testid="evening-plan"
    desc="At 8pm on a nudge day, a gentle phone reminder if today's plan isn't finished yet, so your streak keeps going. Respects your quiet hours." />
);

export const ThanksRecapCard = () => (
  <AlertToggleCard field="thanks_recap_enabled" icon={HandHeart} title="Supporter thanks recap" label="Supporter thanks recap" testid="thanks-recap"
    desc="On the 1st at 10am, supporters who answered you on tough days get a short note saying how many days their messages helped. No feelings or details are shared." />
);

export const WellbeingReminderCard = () => (
  <AlertToggleCard field="wellbeing_reminders" icon={Gauge} title="Wellbeing check reminders" label="Wellbeing reminders" testid="wellbeing-reminders"
    desc="Two weeks after your last wellbeing check, a gentle 10am nudge to see how you're really doing. By notification, or email if notifications are off." />
);
